import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000;  // 15 min
const WARNING_BEFORE    = 2  * 60 * 1000;   // avertissement 2 min avant

const AuthContext = createContext();

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('role');
  localStorage.removeItem('user_id');
};

const getStoredUserFallback = () => {
  const role = localStorage.getItem('role');
  const id = localStorage.getItem('user_id');
  return role && id ? { id, role } : null;
};

export const AuthProvider = ({ children }) => {
  const hasStoredToken = Boolean(localStorage.getItem('token'));
  const initialFallbackUser = getStoredUserFallback();

  const [user, setUser] = useState(() => (hasStoredToken ? initialFallbackUser : null));
  // loading reste true tant que fetchUser n'a pas confirmé/infirmé la session
  const [loading, setLoading] = useState(() => hasStoredToken);
  const [sessionWarning, setSessionWarning] = useState(false);

  const warningTimerRef = useRef(null);
  const logoutTimerRef  = useRef(null);

  const fetchUser = useCallback(async () => {
    const tokenAtCallTime = localStorage.getItem('token');
    try {
      if (tokenAtCallTime) {
        const response = await api.get('/api/auth/me');
        setUser({ ...response.data, id: response.data.id || response.data._id, role: response.data.role });
      } else {
        setUser(null);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        // Vérifie que le token n'a pas été remplacé par un re-login entre-temps
        const tokenNow = localStorage.getItem('token');
        if (tokenNow === tokenAtCallTime) {
          clearStoredAuth();
          setUser(null);
        }
        // Sinon un nouveau login a eu lieu → ne pas écraser la session fraîche
      } else {
        console.warn('Auth check fallback mode', error?.message || error);
        setUser(getStoredUserFallback());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasStoredToken) {
      fetchUser();
    }
  }, [fetchUser, hasStoredToken]);

  // ── Déconnexion automatique après inactivité ──────────────────────────────
  const clearInactivityTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
  }, []);

  const resetInactivityTimers = useCallback(() => {
    if (!localStorage.getItem('token')) return;
    clearInactivityTimers();
    setSessionWarning(false);
    warningTimerRef.current = setTimeout(() => {
      setSessionWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
    logoutTimerRef.current = setTimeout(async () => {
      clearStoredAuth();
      setUser(null);
      setSessionWarning(false);
      // Force un reload complet pour éviter les bugs de reconnexion
      window.location.reload();
    }, INACTIVITY_TIMEOUT);
  }, [clearInactivityTimers]);

  // Démarrer/arrêter les timers selon la session
  useEffect(() => {
    if (user) {
      resetInactivityTimers();
      const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
      const handler = () => resetInactivityTimers();
      events.forEach(e => window.addEventListener(e, handler, { passive: true }));
      return () => {
        clearInactivityTimers();
        events.forEach(e => window.removeEventListener(e, handler));
      };
    } else {
      clearInactivityTimers();
    }
  }, [user, resetInactivityTimers, clearInactivityTimers]);

  // Écoute les déconnexions forcées depuis api.js (401 définitif)
  useEffect(() => {
    const handler = () => {
      setUser(null);
    };
    window.addEventListener('auth:force-logout', handler);
    return () => window.removeEventListener('auth:force-logout', handler);
  }, []);

  // Écoute les déconnexions cross-tab (autre onglet supprime le token)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'token' && !e.newValue) {
        setUser(null);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const extendSession = useCallback(() => {
    setSessionWarning(false);
    resetInactivityTimers();
  }, [resetInactivityTimers]);
  // ─────────────────────────────────────────────────────────────────────────

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('token', response.data.access_token);
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    localStorage.setItem('role', response.data.role);
    localStorage.setItem('user_id', response.data.user_id);
    setUser((currentUser) => ({
      ...currentUser,
      id: response.data.user_id,
      role: response.data.role,
    }));
    return response.data;
  };

  const register = async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    try {
      await api.post('/api/auth/logout', { refresh_token: refreshToken });
    } catch (error) {
      // Local logout must always succeed, even if backend revocation fails.
    } finally {
      clearStoredAuth();
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser: fetchUser, sessionWarning, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
